import React, { useEffect, useMemo, useState } from 'react';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { getLiveLocations, DriverLocation } from '../../../services/driverLocationsApi';
import { listMyCustomerDeliveries, DeliveryStop } from '../../../services/deliveriesApi';
import { formatOrderId } from '../../../utils/formatId';
import styles from './CustomerLiveTracking.module.css';

/**
 * VP-CUST-003 / VP-FIELD-001 (web half): customer-scoped delivery tracking.
 *
 * The admin fleet-map (AdminLiveTracking) shows every driver on the road —
 * wrong experience, and wrong data, for a Customer. This view calls the
 * same GET /driver-locations/live endpoint, but the backend now scopes a
 * Customer's call to just the driver(s) currently out on one of their own
 * active delivery stops (services/driverLocations.ts's
 * getLiveLocationsForCustomer — a real join, replacing the old always-empty
 * stub). Phase-1 scope: a status view rather than a full map integration —
 * still real data, just not plotted on a map yet.
 */

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function initialsOf(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
}

const CustomerLiveTracking: React.FC = () => {
  const [locations, setLocations] = useState<DriverLocation[]>([]);
  const [stops, setStops] = useState<DeliveryStop[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    Promise.all([
      getLiveLocations(),
      listMyCustomerDeliveries({ limit: 20 }).catch(() => [] as DeliveryStop[]),
    ])
      .then(([locs, st]) => {
        setLocations(locs);
        setStops(st);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load tracking data'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeStop = useMemo(
    () => stops.find((s) => s.status === 'in_transit') ?? stops.find((s) => s.status === 'pending') ?? null,
    [stops]
  );

  const primaryLocation = locations[0] ?? null;

  return (
    <div className={styles.container}>
      <AsyncBoundary loading={loading && locations.length === 0 && !activeStop} error={error} empty={false} onRetry={load}>
        <div className={styles.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 className={styles.title}>Your Delivery</h3>
            {primaryLocation && (
              <span className={styles.liveBadge}>
                <span className={styles.dot} />
                Live
              </span>
            )}
          </div>

          {!activeStop && !primaryLocation ? (
            <div className={styles.emptyState}>
              <p>No delivery is currently out for you.</p>
              <p>Once a driver is en route with your order, live status will appear here.</p>
            </div>
          ) : (
            <>
              {primaryLocation && (
                <div className={styles.driverRow}>
                  <div className={styles.avatar}>{initialsOf(primaryLocation.driver_name || 'D')}</div>
                  <div>
                    <div className={styles.driverName}>{primaryLocation.driver_name || `Driver #${primaryLocation.driver_id}`}</div>
                    <div className={styles.driverMeta}>
                      {primaryLocation.route_name || 'On route'} · updated {relativeTime(primaryLocation.captured_at)}
                    </div>
                  </div>
                </div>
              )}

              <div className={styles.statGrid}>
                <div className={styles.stat}>
                  <div className={styles.statLabel}>Order</div>
                  <div className={styles.statValue}>{activeStop ? formatOrderId(activeStop.order_id) : '—'}</div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.statLabel}>Status</div>
                  <div className={styles.statValue}>
                    {activeStop?.status === 'in_transit' ? 'In Transit' : activeStop?.status === 'pending' ? 'Scheduled' : '—'}
                  </div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.statLabel}>Scheduled Window</div>
                  <div className={styles.statValue}>
                    {activeStop?.scheduled_window_start
                      ? new Date(activeStop.scheduled_window_start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                      : '—'}
                  </div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.statLabel}>Driver Speed</div>
                  <div className={styles.statValue}>
                    {primaryLocation?.speed_kmh != null ? `${primaryLocation.speed_kmh.toFixed(0)} km/h` : '—'}
                  </div>
                </div>
              </div>

              {primaryLocation && (
                <p style={{ marginTop: 14, fontSize: 12, color: '#9CA3AF' }}>
                  Last known position: {primaryLocation.latitude.toFixed(4)}, {primaryLocation.longitude.toFixed(4)}
                </p>
              )}
            </>
          )}
        </div>
      </AsyncBoundary>
    </div>
  );
};

export const CustomerLiveTrackingPage: React.FC = () => <CustomerLiveTracking />;

export { CustomerLiveTracking };
export default CustomerLiveTracking;
